import React, { useEffect } from 'react';
import styled from 'styled-components';
import { usePageStore } from '../../../shared/store/pageStore';
import { useAuth } from '../../../shared/hooks/useApi/useAuth';
import { useAdminMemberListStore } from '../../share/store/adminMemberStore';
import { useAdminMember } from '../../share/hook/useAdminMember';
import { LIMIT_PAGE_NUM } from '../../../shared/constants/limits';
import { PerPage } from '../../../shared/enum/EnumRepository';
import PageSizeStation from '../../../shared/component/PageSizeStation';

const PageAboutMember: React.FC = () => {
  const { page } = usePageStore();
  const [currentWindow, setCurrentWindow] = React.useState(1);
  const { getMemberList } = useAdminMember();
  const { user } = useAuth();
  const { pageSize, setPageSize, sortData, filterData } = useAdminMemberListStore();

  const handlePageClick = (pageNum: number) => {
    if (!user) return;
    getMemberList(user.aui, { page: pageNum, size: pageSize, sortData: sortData, filterData: filterData });
  };

  const handlePageWindowClick = (direction: 'prev' | 'next') => {
    if (!user) return;
    if (direction === 'prev' && currentWindow > 1) {
      const newPage = (currentWindow - 2) * LIMIT_PAGE_NUM + 1;
      getMemberList(user.aui, { page: newPage, size: pageSize, sortData: sortData, filterData: filterData });
      setCurrentWindow(currentWindow - 1);
    } else if (direction === 'next' && currentWindow < Math.ceil(page.totalPages / LIMIT_PAGE_NUM)) {
      const newPage = currentWindow * LIMIT_PAGE_NUM + 1;
      getMemberList(user.aui, { page: newPage, size: pageSize, sortData: sortData, filterData: filterData });
      setCurrentWindow(currentWindow + 1);
    }
  };

  // Calculate the start and end page numbers for the current window
  const startPage = (currentWindow - 1) * LIMIT_PAGE_NUM + 1;
  const endPage = Math.min(startPage + LIMIT_PAGE_NUM - 1, page.totalPages);

  // Update window if current page is outside the visible range
  useEffect(() => {
    const windowForCurrentPage = Math.ceil(page.page / LIMIT_PAGE_NUM);
    if (windowForCurrentPage !== currentWindow) {
      setCurrentWindow(windowForCurrentPage);
    }
  }, [page.page]);

  const handlePerPageChange = (value: PerPage) => {
    setPageSize(value);
  }

  return (
    <PageContainer>
      <PageButtonContainer>
        {currentWindow > 1 && (
          <PageWindowButton onClick={() => handlePageWindowClick('prev')}>←</PageWindowButton>
        )}
        {Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i)
          .map((pageNum) => (
            pageNum === page.page ? (
              <CurrPageButton key={pageNum}>{pageNum}</CurrPageButton>
            ) : (
              <PageButton key={pageNum} onClick={() => handlePageClick(pageNum)}>{pageNum}</PageButton>
            )
          ))}
        {currentWindow < Math.ceil(page.totalPages / LIMIT_PAGE_NUM) && (
          <PageWindowButton onClick={() => handlePageWindowClick('next')}>→</PageWindowButton>
        )}
      </PageButtonContainer>
      <PageSizeStation 
        currentValue={pageSize} 
        handlePerPageChange={handlePerPageChange} 
        direction={true} />
    </PageContainer>
  );
};

export default PageAboutMember;

const PageContainer = styled.div`
  width: 100%;
  height: calc(2vh);
  display: flex;
  align-items: center;
  gap: 30px;
  ${({ theme }) => theme.typography.Body_04};
`;

export const PageButtonContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

export const PageButton = styled.div`
  width: fit-content;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding-top: 4px;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.color_Gray_05};
  transition: all 0.2s ease-in;
  &:hover {
    transform: scale(1.1);
    color: ${({ theme }) => theme.colors.color_Gray_04};
  }
`;

export const CurrPageButton = styled(PageButton)`
  color: ${({ theme }) => theme.colors.color_Gray_02};
  cursor: default;
`;

export const PageWindowButton = styled(PageButton)`
`;